import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useContactModal } from '../context/ContactModalContext';
import useSeo from '../hooks/useSeo';
import '../styles/pages/HomeStudio.css';

/* ============================================================
   Animated artwork - solid colours, CSS / SMIL motion.
   ============================================================ */

/* Full-page animated background */
function PageBg() {
  return (
    <div className="sh-page-bg" aria-hidden="true">
      <svg className="sh-page-bg-svg" viewBox="0 0 1440 2400" preserveAspectRatio="xMidYMin slice" fill="none">
        <g stroke="#2a5a82" fill="none" strokeWidth="1.4">
          <path
            className="sh-bg-path"
            d="M80 1400c180-80 320 40 480-20s280-120 480-40 280 160 400 80"
            strokeDasharray="6 14"
            opacity="0.3"
          />
          <path
            className="sh-bg-path sh-bg-path--delay"
            d="M60 2100c200 60 360-80 560-20s320 140 520 40"
            strokeDasharray="5 12"
            opacity="0.26"
          />
          <path
            className="sh-bg-path"
            d="M40 520c160 40 280-60 420-20s240 100 400 40 260-80 380-20"
            strokeDasharray="4 11"
            opacity="0.22"
          />
        </g>
      </svg>
    </div>
  );
}

/* Hero background - full-bleed IDE with code being typed */
function HeroWash() {
  // VS Code dark+ palette
  const KW = '#569cd6';
  const KP = '#c586c0';
  const FN = '#dcdcaa';
  const ST = '#ce9178';
  const VR = '#9cdcfe';
  const NM = '#b5cea8';
  const TX = '#d4d4d4';
  const CM = '#6a9955';

  // Each line: indent + tokens [width, colour] - span most of the editor
  const lines = [
    { i: 0, t: [[58, KP], [78, VR], [40, KP], [150, ST], [18, TX], [96, VR], [64, ST], [22, TX], [110, FN], [48, NM]] },
    { i: 0, t: [[280, CM]] },
    { i: 0, t: [[82, KW], [110, FN], [24, TX], [20, TX], [72, VR], [140, ST], [16, TX], [88, VR], [54, ST]] },
    { i: 1, t: [[54, KW], [92, VR], [20, TX], [120, FN], [38, NM], [16, TX], [86, VR], [100, ST], [18, TX], [70, FN]] },
    { i: 1, t: [[68, KP], [96, VR], [26, TX], [160, ST], [20, TX], [74, KW], [90, VR]] },
    { i: 2, t: [[38, KW], [108, VR], [96, ST], [18, TX], [82, FN], [44, NM], [16, TX], [120, ST], [60, VR]] },
    { i: 3, t: [[36, KW], [140, TX], [46, KW], [92, VR], [68, ST], [20, TX], [86, FN]] },
    { i: 3, t: [[72, KW], [100, VR], [22, TX], [118, FN], [32, TX], [78, VR], [94, ST]] },
    { i: 2, t: [[52, KW], [84, VR], [20, TX], [106, ST], [18, TX], [70, FN], [40, NM]] },
    { i: 1, t: [[16, TX]] },
    { i: 0, t: [[16, TX]] },
    { i: 0, t: [[62, KP], [76, KW], [70, VR], [24, TX], [110, FN], [52, NM], [18, TX], [98, VR], [130, ST]] },
    { i: 0, t: [[240, CM]] },
    { i: 0, t: [[78, KW], [120, FN], [26, TX], [94, VR], [150, ST], [18, TX], [82, KW], [70, VR]] },
    { i: 1, t: [[54, KW], [88, VR], [108, ST], [20, TX], [86, FN], [16, TX], [96, VR], [72, ST]] },
    { i: 1, t: [[60, KP], [96, VR], [24, TX], [130, ST], [44, TX], [78, FN], [36, NM], [90, VR]] },
    { i: 2, t: [[44, KW], [140, TX], [76, VR], [104, FN], [20, TX], [88, ST], [56, VR]] },
    { i: 2, t: [[38, KW], [84, VR], [20, TX], [118, ST], [16, TX], [70, FN], [92, VR]] },
    { i: 1, t: [[16, TX]] },
    { i: 0, t: [[58, KP], [90, VR], [50, KP], [160, ST], [22, TX], [100, VR], [64, ST], [18, TX], [86, FN]] },
  ];

  const x0 = 112;
  const y0 = 116;
  const lineH = 32;
  const indentW = 22;
  const gap = 8;
  const tokenH = 12;
  const gutterX = 58;

  let gi = 0;

  return (
    <svg className="sh-hero-ide" viewBox="0 0 1200 820" preserveAspectRatio="xMinYMid slice" fill="none" aria-hidden="true">
      <g className="sh-ide-window">
        <rect width="1200" height="820" fill="#1e1e1e" />

        {/* Title bar */}
        <rect width="1200" height="44" fill="#323233" />
        <circle cx="26" cy="22" r="6.5" fill="#ff5f56" />
        <circle cx="50" cy="22" r="6.5" fill="#ffbd2e" />
        <circle cx="74" cy="22" r="6.5" fill="#27c93f" />

        {/* Activity bar */}
        <rect y="44" width="48" height="752" fill="#252526" />
        <rect x="12" y="64" width="24" height="20" rx="3" fill="#6a6a6a" />
        <rect x="12" y="98" width="24" height="20" rx="3" fill="#454545" />
        <rect x="12" y="132" width="24" height="20" rx="3" fill="#454545" />
        <rect x="12" y="166" width="24" height="20" rx="3" fill="#454545" />
        <rect x="12" y="200" width="24" height="20" rx="3" fill="#454545" />

        {/* Tab strip */}
        <rect x="48" y="44" width="1152" height="32" fill="#2d2d2d" />
        <rect x="48" y="44" width="168" height="32" fill="#1e1e1e" />
        <rect x="48" y="44" width="168" height="2" fill="#519aba" />
        <circle cx="68" cy="60" r="4" fill="#519aba" />
        <rect x="82" y="56" width="96" height="8" rx="2" fill="#c5c5c5" opacity="0.75" />
        <rect x="230" y="56" width="72" height="8" rx="2" fill="#858585" opacity="0.55" />

        {/* Gutter */}
        <rect x="48" y="76" width="56" height="720" fill="#1e1e1e" />
        {lines.map((_, idx) => (
          <rect
            key={`g${idx}`}
            x={gutterX}
            y={y0 + idx * lineH - 4}
            width={idx + 1 >= 10 ? 16 : 12}
            height="7"
            rx="1.5"
            fill="#6a6a6a"
            opacity="0.45"
          />
        ))}

        {/* Code lines */}
        {lines.map((line, idx) => {
          const lineY = y0 + idx * lineH;
          let tx = x0 + line.i * indentW;
          return (
            <g key={`l${idx}`}>
              {line.t.map((tok, ti) => {
                const [w, c] = tok;
                const rect = (
                  <rect
                    key={ti}
                    className="sh-ide-token"
                    x={tx}
                    y={lineY - tokenH / 2}
                    width={w}
                    height={tokenH}
                    rx="3"
                    fill={c}
                    style={{ animationDelay: `${(gi * 0.04).toFixed(2)}s` }}
                  />
                );
                tx += w + gap;
                gi += 1;
                return rect;
              })}
            </g>
          );
        })}

        {/* Blinking caret */}
        <g className="sh-ide-caret">
          <rect x={x0} y={y0 - tokenH / 2} width="2.5" height={tokenH} rx="1" fill="#aeafad" />
        </g>

        {/* Status bar */}
        <rect y="796" width="1200" height="24" fill="#007acc" />
        <rect x="18" y="804" width="52" height="8" rx="2" fill="#ffffff" opacity="0.85" />
        <rect x="82" y="804" width="34" height="8" rx="2" fill="#ffffff" opacity="0.6" />
        <rect x="1120" y="804" width="60" height="8" rx="2" fill="#ffffff" opacity="0.6" />
      </g>
    </svg>
  );
}

/* Hero art - handwritten phrase */
function HeroHandwriteArt() {
  const lines = ['a tech', 'innovation', 'studio'];
  const svgRef = useRef(null);
  const [playKey, setPlayKey] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const node = svgRef.current;
    if (!node) return undefined;

    const hero = node.closest('.sh-hero') || node;
    let visible = false;
    let hasPlayed = false;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!visible) {
            visible = true;
            if (hasPlayed) {
              setPlayKey((k) => k + 1);
            }
            hasPlayed = true;
            setPlaying(true);
          }
        } else if (visible) {
          visible = false;
          setPlaying(false);
        }
      },
      { threshold: 0.28 }
    );

    io.observe(hero);
    return () => io.disconnect();
  }, []);

  return (
    <svg
      key={playKey}
      ref={svgRef}
      className={`sh-hero-art sh-hw${playing ? ' sh-hw--play' : ''}`}
      viewBox="0 0 480 280"
      fill="none"
      aria-hidden="true"
      role="img"
    >
      <title>a tech innovation studio</title>
      {lines.map((line, i) => {
        const y = 72 + i * 78;
        return (
          <g key={line} style={{ '--hw-delay': `${i * 0.65}s` }}>
            <text
              className="sh-hw-text sh-hw-text--outline"
              x="460"
              y={y}
              textAnchor="end"
              fontFamily="Caveat, cursive"
              fontSize="68"
              fontWeight="500"
            >
              {line}
            </text>
            <text
              className="sh-hw-text sh-hw-text--fill"
              x="460"
              y={y}
              textAnchor="end"
              fontFamily="Caveat, cursive"
              fontSize="68"
              fontWeight="500"
            >
              {line}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* Vertical illustrations - currentColor is --v-ink; accents via --v-mark, tints via --v-soft */
function IconFintech() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Card */}
      <rect x="4" y="14" width="28" height="20" rx="3" fill="var(--v-soft)" stroke="currentColor" strokeWidth="2" />
      <path d="M4 20h28" stroke="currentColor" strokeWidth="2" />
      <rect x="8" y="24" width="7" height="5" rx="1" fill="var(--v-mark)" />
      <path d="M8 31h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.55" />
      {/* Coin */}
      <circle className="sh-icon-blink" cx="36" cy="32" r="8.5" fill="var(--v-mark)" stroke="currentColor" strokeWidth="2" />
      <path d="M36 27.5v9M33 29.8h5.4c-3.6 0-5.4 1-5.4 2.2s1.8 2.2 5.4 2.2H33" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
      {/* Spark line */}
      <path
        className="sh-dash-flow"
        d="M6 10c6 2 10-4 16-3s9 4 20 0"
        stroke="var(--v-mark)"
        strokeWidth="2"
        strokeDasharray="2 4"
        strokeLinecap="round"
      />
      <path d="m38 4 4 3-4.6 1.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCommerce() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Storefront awning */}
      <path d="M6 15 9 7h30l3 8H6Z" fill="var(--v-mark)" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M15 7v8M24 7v8M33 7v8" stroke="#fff" strokeWidth="1.6" opacity="0.85" />
      {/* Shop body */}
      <path d="M8 15v25h32V15" fill="var(--v-soft)" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M6 40h36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Window with goods */}
      <rect x="12" y="20" width="12" height="10" rx="1.5" fill="#fff" stroke="currentColor" strokeWidth="1.7" />
      <g className="sh-icon-bars">
        <path d="M15 27v-3M18 27v-5M21 27v-2" stroke="var(--v-mark)" strokeWidth="2" strokeLinecap="round" />
      </g>
      {/* Door */}
      <path d="M29 40V24h8v16" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <circle cx="31.5" cy="32" r="1.2" fill="var(--v-mark)" />
      {/* Cart badge */}
      <g className="sh-icon-rise">
        <circle cx="39" cy="30" r="7.5" fill="var(--v-mark)" stroke="currentColor" strokeWidth="1.6" />
        <path d="M35.6 27.4h1.5l1 4.6h3.6l1-3.1h-5.4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="38.2" cy="34.2" r="1" fill="#fff" />
        <circle cx="41.2" cy="34.2" r="1" fill="#fff" />
      </g>
    </svg>
  );
}

function IconLogistics() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Truck */}
      <rect x="4" y="16" width="22" height="16" rx="1.5" fill="var(--v-soft)" stroke="currentColor" strokeWidth="2" />
      <path d="M26 21h9l6 7v4h-6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M26 32h4" stroke="currentColor" strokeWidth="2" />
      <circle cx="13" cy="34" r="3.6" fill="#fff" stroke="currentColor" strokeWidth="2" />
      <circle cx="34" cy="34" r="3.6" fill="#fff" stroke="currentColor" strokeWidth="2" />
      <path d="M9 21h9M9 25h6" stroke="var(--v-mark)" strokeWidth="2" strokeLinecap="round" />
      {/* Motion + pin */}
      <path className="sh-dash-flow" d="M2 38c8 3 20 3 30 0" stroke="var(--v-mark)" strokeWidth="1.8" strokeDasharray="3 4" strokeLinecap="round" />
      <g className="sh-icon-blink">
        <path d="M41 4a5.5 5.5 0 0 1 5.5 5.5C46.5 13.5 41 18 41 18s-5.5-4.5-5.5-8.5A5.5 5.5 0 0 1 41 4Z" fill="var(--v-mark)" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="41" cy="9.5" r="1.8" fill="#fff" />
      </g>
    </svg>
  );
}

function IconEdtech() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Open book */}
      <path d="M24 18c-4-3-10-3.6-17-3v24c7-.6 13 0 17 3 4-3 10-3.6 17-3V15c-7-.6-13 0-17 3Z" fill="var(--v-soft)" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M24 18v24" stroke="currentColor" strokeWidth="2" />
      <path d="M11 22.5c3-.4 6-.3 9 .6M11 28c3-.4 6-.3 9 .6M28 23c3-.9 6-1 9-.6M28 28.6c3-.9 6-1 9-.6" stroke="var(--v-mark)" strokeWidth="1.7" strokeLinecap="round" opacity="0.9" />
      {/* Grad cap */}
      <g className="sh-icon-rise">
        <path d="M24 3 38 8.5 24 14 10 8.5 24 3Z" fill="var(--v-mark)" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        <path d="M17 11v4c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M38 8.5V15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <circle className="sh-icon-blink" cx="38" cy="16.6" r="1.9" fill="var(--v-mark)" stroke="currentColor" strokeWidth="1.2" />
      </g>
    </svg>
  );
}

function IconAI() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Chip body */}
      <rect x="11" y="11" width="26" height="26" rx="4" fill="var(--v-soft)" stroke="currentColor" strokeWidth="2" />
      {/* Pins */}
      <path d="M18 11V5M24 11V5M30 11V5M18 43v-6M24 43v-6M30 43v-6M11 18H5M11 24H5M11 30H5M43 18h-6M43 24h-6M43 30h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.65" />
      {/* Neural nodes */}
      <path d="M18 20l6 4 6-4M18 28l6-4 6 4M18 20v8M30 20v8" stroke="var(--v-mark)" strokeWidth="1.8" />
      <circle cx="18" cy="20" r="2.4" fill="var(--v-mark)" />
      <circle className="sh-icon-blink" cx="24" cy="24" r="2.8" fill="currentColor" />
      <circle cx="30" cy="20" r="2.4" stroke="var(--v-mark)" strokeWidth="1.8" fill="#fff" />
      <circle cx="18" cy="28" r="2.4" stroke="var(--v-mark)" strokeWidth="1.8" fill="#fff" />
      <circle className="sh-icon-blink-delay" cx="30" cy="28" r="2.4" fill="var(--v-mark)" />
    </svg>
  );
}

function IconEnterprise() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      {/* Towers */}
      <rect x="6" y="14" width="16" height="28" fill="var(--v-soft)" stroke="currentColor" strokeWidth="2" />
      <rect x="22" y="6" width="14" height="36" fill="#fff" stroke="currentColor" strokeWidth="2" />
      <path d="M10 20h3M15 20h3M10 26h3M15 26h3M10 32h3M15 32h3" stroke="var(--v-mark)" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M26 12h6M26 18h6M26 24h6M26 30h6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" opacity="0.5" />
      <rect x="27" y="36" width="4" height="6" fill="var(--v-mark)" />
      {/* Check badge */}
      <g className="sh-icon-rise">
        <circle cx="39" cy="33" r="7.5" fill="var(--v-mark)" stroke="currentColor" strokeWidth="1.8" />
        <path d="m35.8 33 2.3 2.4 4.2-4.6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

/* Animated digitised globe backdrop for "What we build" */
function WhatGlobeBg() {
  return (
    <svg
      className="sh-what-globe"
      viewBox="0 0 800 800"
      preserveAspectRatio="xMidYMid meet"
      fill="none"
      aria-hidden="true"
    >
      <defs>
        <clipPath id="sh-globe-clip">
          <circle cx="400" cy="400" r="296" />
        </clipPath>
      </defs>

      {/* Wireframe: parallels + meridians */}
      <g stroke="#5aa8e8" strokeWidth="1.4" opacity="0.7" clipPath="url(#sh-globe-clip)">
        <ellipse cx="400" cy="400" rx="296" ry="296" />
        <ellipse cx="400" cy="400" rx="296" ry="108" />
        <ellipse cx="400" cy="400" rx="296" ry="206" />
        <ellipse cx="400" cy="260" rx="252" ry="62" />
        <ellipse cx="400" cy="540" rx="252" ry="62" />
        <ellipse cx="400" cy="400" rx="108" ry="296" />
        <ellipse cx="400" cy="400" rx="206" ry="296" />
      </g>
      <circle cx="400" cy="400" r="296" stroke="#6eb8f0" strokeWidth="1.8" opacity="0.75" />

      {/* Rotating orbital rings */}
      <g className="sh-globe-ring">
        <circle cx="400" cy="400" r="336" stroke="#7ec8f5" strokeWidth="1.5" strokeDasharray="4 14" opacity="0.75" />
      </g>
      <g className="sh-globe-ring sh-globe-ring--rev">
        <circle cx="400" cy="400" r="372" stroke="#4a90c8" strokeWidth="1.3" strokeDasharray="2 10" opacity="0.55" />
      </g>

      {/* Service arcs between nodes */}
      <g strokeLinecap="round" fill="none">
        <path className="sh-globe-arc" d="M244 306 C 320 180, 500 190, 566 300" stroke="#3dcc8e" strokeWidth="2.2" />
        <path className="sh-globe-arc sh-globe-arc--d1" d="M212 452 C 330 560, 520 560, 596 430" stroke="#f0a84a" strokeWidth="2.2" />
        <path className="sh-globe-arc sh-globe-arc--d2" d="M300 540 C 380 420, 470 350, 570 336" stroke="#b08eef" strokeWidth="2.2" />
        <path className="sh-globe-arc sh-globe-arc--d3" d="M250 350 C 340 300, 460 470, 540 480" stroke="#f07ab8" strokeWidth="2.2" />
      </g>

      {/* Nodes */}
      <g>
        <circle className="sh-globe-node" cx="244" cy="306" r="6" fill="#3dcc8e" />
        <circle className="sh-globe-node sh-globe-node--d1" cx="566" cy="300" r="6" fill="#3dd4c4" />
        <circle className="sh-globe-node sh-globe-node--d2" cx="212" cy="452" r="6" fill="#f0a84a" />
        <circle className="sh-globe-node sh-globe-node--d3" cx="596" cy="430" r="6" fill="#b08eef" />
        <circle className="sh-globe-node sh-globe-node--d1" cx="300" cy="540" r="6" fill="#f07ab8" />
        <circle className="sh-globe-node sh-globe-node--d2" cx="570" cy="336" r="5" fill="#5aa8e8" />
        <circle className="sh-globe-node sh-globe-node--d3" cx="540" cy="480" r="5" fill="#3dcc8e" />
      </g>

      {/* Orbiting satellites */}
      <g className="sh-globe-orbit">
        <circle cx="400" cy="64" r="7" fill="#7ec8f5" />
        <circle cx="400" cy="64" r="12" stroke="#7ec8f5" strokeWidth="1.4" opacity="0.7" />
      </g>
      <g className="sh-globe-orbit sh-globe-orbit--rev">
        <circle cx="400" cy="28" r="5" fill="#f0a84a" />
      </g>
    </svg>
  );
}

/* Process glyphs - stroke icons, no fill discs */
function GlyphTalk() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path
        d="M8 12h28a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H22l-8 7v-7H8a3 3 0 0 1-3-3V15a3 3 0 0 1 3-3Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path className="sh-glyph-type" d="M14 20h16M14 26h10" stroke="var(--st-accent-bright)" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function GlyphPlan() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <rect x="10" y="6" width="28" height="36" rx="3" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16h16M16 23h16M16 30h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
      <rect className="sh-glyph-fill" x="27" y="27" width="9" height="9" rx="1.5" fill="var(--st-accent-bright)" />
    </svg>
  );
}

function GlyphBuild() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path d="m16 14-10 10 10 10M32 14l10 10-10 10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path className="sh-glyph-slash" d="m29 10-10 28" stroke="var(--st-accent-bright)" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function GlyphLaunch() {
  return (
    <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path d="M24 38V14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
      <path className="sh-glyph-up" d="m14 24 10-10 10 10" stroke="var(--st-accent-bright)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 38h22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.65" />
    </svg>
  );
}

/* Animated backdrop for plans - clean orbital atom */
function PlansBg() {
  return (
    <svg className="sh-plans-bg-svg" viewBox="0 0 1200 720" preserveAspectRatio="xMidYMid slice" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id="sh-plans-glow" cx="58%" cy="48%" r="48%">
          <stop offset="0%" stopColor="#6ba8d4" stopOpacity="0.18" />
          <stop offset="50%" stopColor="#3e7cb1" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#3e7cb1" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sh-atom-core" cx="38%" cy="32%" r="68%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="40%" stopColor="#8ec8f0" />
          <stop offset="100%" stopColor="#3e7cb1" />
        </radialGradient>
        <radialGradient id="sh-atom-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#6ba8d4" stopOpacity="0.45" />
          <stop offset="55%" stopColor="#6ba8d4" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#6ba8d4" stopOpacity="0" />
        </radialGradient>
      </defs>

      <ellipse cx="720" cy="360" rx="400" ry="300" fill="url(#sh-plans-glow)" />

      <g className="sh-atom" transform="translate(720 360)">
        {/* Orbit A - horizontal */}
        <g>
          <ellipse rx="260" ry="92" stroke="#57b4e8" strokeWidth="1.6" opacity="0.55" />
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 0 0"
              to="360 0 0"
              dur="7s"
              repeatCount="indefinite"
            />
            <circle cx="260" cy="0" r="5.5" fill="#57b4e8" />
            <circle cx="260" cy="0" r="10" fill="#57b4e8" opacity="0.22" />
          </g>
        </g>

        {/* Orbit B - tilted +60° */}
        <g transform="rotate(60)">
          <ellipse rx="260" ry="92" stroke="#2dbdb0" strokeWidth="1.6" opacity="0.5" />
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 0 0"
              to="-360 0 0"
              dur="9s"
              repeatCount="indefinite"
            />
            <circle cx="260" cy="0" r="5.5" fill="#2dbdb0" />
            <circle cx="260" cy="0" r="10" fill="#2dbdb0" opacity="0.22" />
          </g>
        </g>

        {/* Orbit C - tilted -60° */}
        <g transform="rotate(-60)">
          <ellipse rx="260" ry="92" stroke="#9b7fd4" strokeWidth="1.6" opacity="0.5" />
          <g>
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="120 0 0"
              to="480 0 0"
              dur="11s"
              repeatCount="indefinite"
            />
            <circle cx="260" cy="0" r="5.5" fill="#9b7fd4" />
            <circle cx="260" cy="0" r="10" fill="#9b7fd4" opacity="0.22" />
          </g>
        </g>

        {/* Single nucleus */}
        <circle className="sh-atom-halo" r="54" fill="url(#sh-atom-halo)" />
        <circle className="sh-atom-nucleus" r="26" fill="url(#sh-atom-core)" />
        <circle cx="-7" cy="-8" r="5" fill="#ffffff" opacity="0.55" />
      </g>
    </svg>
  );
}

/* Soft tree silhouette - trunk + layered canopy (local coords, origin at base) */
function AboutTree({ x, y, scale = 1, opacity = 0.28 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M0 0V-28" stroke="#3d5c4a" strokeWidth="2.2" strokeLinecap="round" />
      <ellipse cx="0" cy="-40" rx="16" ry="14" fill="#4f7a5e" />
      <ellipse cx="-8" cy="-32" rx="11" ry="9" fill="#5a8a68" />
      <ellipse cx="9" cy="-33" rx="10" ry="8" fill="#3f6b52" />
      <ellipse cx="0" cy="-50" rx="9" ry="8" fill="#6a9a76" />
    </g>
  );
}

/* Animated backdrop for about - soft sun, trees, chart marks */
function AboutCoastBg() {
  return (
    <svg
      className="sh-about-bg-svg sh-about-bg-svg--desktop"
      viewBox="0 0 1200 720"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
    >
      {/* Soft sun — inset from RHS; slice crop eats the viewBox edges on wide desktops */}
      <circle className="sh-about-sun" cx="880" cy="148" r="54" fill="#e8b86d" opacity="0.28" />
      <circle cx="880" cy="148" r="28" fill="#f0c87a" opacity="0.35" />

      {/* Grove under sun — rightmost canopy stays inside ~980 (safe for side slice) */}
      <g className="sh-about-trees" aria-hidden="true">
        <AboutTree x={800} y={360} scale={1.15} opacity={0.22} />
        <AboutTree x={845} y={378} scale={1.55} opacity={0.3} />
        <AboutTree x={890} y={368} scale={1.35} opacity={0.26} />
        <AboutTree x={935} y={382} scale={1.55} opacity={0.32} />
        <AboutTree x={975} y={370} scale={1.2} opacity={0.24} />
      </g>

      {/* Sparse geo / chart marks */}
      <g stroke="#2a5a82" fill="none" opacity="0.2">
        <path d="M160 280 220 210 290 250 360 170 430 230" strokeWidth="1.4" strokeLinejoin="round" />
        <circle className="sh-about-node" cx="160" cy="280" r="3.5" fill="#3e7cb1" stroke="none" />
        <circle className="sh-about-node sh-about-node--d1" cx="220" cy="210" r="3.5" fill="#3e7cb1" stroke="none" />
        <circle className="sh-about-node sh-about-node--d2" cx="290" cy="250" r="3.5" fill="#3e7cb1" stroke="none" />
        <circle className="sh-about-node sh-about-node--d3" cx="360" cy="170" r="3.5" fill="#3e7cb1" stroke="none" />
        <circle className="sh-about-node" cx="430" cy="230" r="3.5" fill="#3e7cb1" stroke="none" />
      </g>
    </svg>
  );
}

/* Portrait about backdrop - composed for narrow viewports */
function AboutCoastBgMobile() {
  return (
    <svg
      className="sh-about-bg-svg sh-about-bg-svg--mobile"
      viewBox="0 0 390 844"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
      aria-hidden="true"
    >
      {/* Soft sun - kept inside the mobile frame */}
      <circle className="sh-about-sun sh-about-sun--mobile" cx="312" cy="118" r="48" fill="#e8b86d" opacity="0.34" />
      <circle cx="312" cy="118" r="24" fill="#f0c87a" opacity="0.4" />

      {/* Grove below the sun — inset from right edge for portrait crop */}
      <g className="sh-about-trees" aria-hidden="true">
        <AboutTree x={248} y={280} scale={1} opacity={0.26} />
        <AboutTree x={285} y={295} scale={1.35} opacity={0.34} />
        <AboutTree x={322} y={286} scale={1.15} opacity={0.28} />
        <AboutTree x={350} y={298} scale={0.95} opacity={0.24} />
      </g>

      {/* Geo / chart marks - upper mid, readable on portrait */}
      <g stroke="#2a5a82" fill="none" opacity="0.28">
        <path d="M48 220 98 168 152 198 206 140 268 186" strokeWidth="1.6" strokeLinejoin="round" />
        <circle className="sh-about-node" cx="48" cy="220" r="4" fill="#3e7cb1" stroke="none" />
        <circle className="sh-about-node sh-about-node--d1" cx="98" cy="168" r="4" fill="#3e7cb1" stroke="none" />
        <circle className="sh-about-node sh-about-node--d2" cx="152" cy="198" r="4" fill="#3e7cb1" stroke="none" />
        <circle className="sh-about-node sh-about-node--d3" cx="206" cy="140" r="4" fill="#3e7cb1" stroke="none" />
        <circle className="sh-about-node" cx="268" cy="186" r="4" fill="#3e7cb1" stroke="none" />
      </g>
    </svg>
  );
}

/* ============================================================
   Content
   ============================================================ */

const ABOUT_DOMAINS = [
  { name: 'Geospatial', blurb: 'Maps, places, and location-aware systems.' },
  { name: 'Research', blurb: 'Experiments that turn into products.' },
  { name: 'Finance', blurb: 'Money flows, ledgers, and controls.' },
  { name: 'Operations', blurb: 'Field work, teams, and daily systems.' },
  { name: 'Compliance', blurb: 'Rules built into the product, not bolted on.' },
];

const VERTICALS = [
  { tone: 'fintech', icon: <IconFintech />, name: 'Fintech', blurb: 'Payments, lending, and money tools.' },
  { tone: 'commerce', icon: <IconCommerce />, name: 'E-commerce', blurb: 'Stores, orders, and billing.' },
  { tone: 'logistics', icon: <IconLogistics />, name: 'Logistics', blurb: 'Fleet, routes, and deliveries.' },
  { tone: 'edtech', icon: <IconEdtech />, name: 'Edtech', blurb: 'Learning platforms and assessments.' },
  { tone: 'ai', icon: <IconAI />, name: 'AI & Data', blurb: 'Useful AI inside daily work.' },
  { tone: 'ops', icon: <IconEnterprise />, name: 'Enterprise Ops', blurb: 'Attendance, field teams, expenses.' },
];

const PROCESS = [
  { glyph: <GlyphTalk />, name: 'Talk', detail: 'We listen and figure out what you need.' },
  { glyph: <GlyphPlan />, name: 'Plan', detail: 'Scope, screens, and a fixed price.' },
  { glyph: <GlyphBuild />, name: 'Build', detail: 'Bi weekly beta releases.' },
  { glyph: <GlyphLaunch />, name: 'Launch', detail: 'We ship it - and stay around to help.' },
];

function Home() {
  const { openContactModal } = useContactModal();
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      return () => clearTimeout(t);
    }
  }, [hash]);

  const seoConfig = useMemo(() => {
    const pageTitle = 'BloomNav | A Technology Studio';
    const pageDescription =
      'BloomNav is a tech research lab and innovation studio in Goa - geospatial data, research, finance, operations, and compliance, from development through testing and deployment.';

    return {
      title: pageTitle,
      description: pageDescription,
      canonicalPath: '/',
      ogType: 'website',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav',
      jsonLd: [
        {
          id: 'schema-home-studio',
          data: {
            '@context': 'https://schema.org',
            '@type': 'ProfessionalService',
            name: 'BloomNav',
            url: `${window.location.origin}/`,
            description: pageDescription,
            areaServed: 'Worldwide',
            address: {
              '@type': 'PostalAddress',
              addressLocality: 'Goa',
              addressCountry: 'IN',
            },
            knowsAbout: [
              'Geospatial data',
              'Research',
              'Finance',
              'Operations',
              'Compliance',
              'Fintech',
              'E-commerce',
              'Logistics',
              'Edtech',
              'AI',
              'Enterprise software',
            ],
            makesOffer: {
              '@type': 'Offer',
              name: 'First version',
              description: 'A working app designed, built and launched in 4 months.',
              price: '16000',
              priceCurrency: 'USD',
            },
            parentOrganization: {
              '@type': 'Organization',
              name: 'CLIVEXCI SYSTEMS LLP',
            },
          },
        },
      ],
    };
  }, []);

  useSeo(seoConfig);

  return (
    <div className="studio-home">
      <PageBg />

      {/* ================= HERO ================= */}
      <section className="sh-hero" data-nav-tone="dark">
        <div className="sh-hero-bg" aria-hidden="true">
          <HeroWash />
        </div>
        <div className="sh-hero-copy">
          <h1 className="sh-hero-brand">BloomNav</h1>
          <p className="sh-hero-motto">
            Consult
            <span className="sh-hero-motto-dot" aria-hidden="true" />
            Innovate
            <span className="sh-hero-motto-dot" aria-hidden="true" />
            Create
          </p>
          <p className="sh-hero-sub">
            We take you from <strong className="sh-em">ideation</strong> to{' '}
            <strong className="sh-em">production</strong>.
          </p>
        </div>
        <div className="sh-hero-visual" aria-hidden="true">
          <div className="sh-hero-art-wrap">
            <HeroHandwriteArt />
          </div>
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section className="sh-section sh-about" id="about" data-nav-tone="light">
        <div className="sh-about-bg" aria-hidden="true">
          <AboutCoastBg />
          <AboutCoastBgMobile />
        </div>
        <header className="sh-section-head sh-about-head">
          <h2>
            About <em>us</em>
          </h2>
        </header>

        <div className="sh-about-body">
          <p className="sh-about-lead">
            BloomNav is a tech research lab and innovation studio.
          </p>
          <p className="sh-about-support">
            We live on Goa&rsquo;s coast - where the mind is calm, the sea is refreshing, and
            systems are always running.
          </p>
          <p className="sh-about-support">
            With experience across geospatial data, research, finance, operations, and compliance.
            We take products through development, testing, and deployment - and stay close after
            they land in production.
          </p>
        </div>

        <ul className="sh-about-domains">
          {ABOUT_DOMAINS.map((d) => (
            <li key={d.name}>
              <h3>{d.name}</h3>
              <p>{d.blurb}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* ================= WHAT WE BUILD ================= */}
      <section className="sh-section sh-what" id="what" data-nav-tone="light">
        <div className="sh-what-bg" aria-hidden="true">
          <WhatGlobeBg />
        </div>
        <header className="sh-section-head">
          <h2>
            What we <em>build.</em>
          </h2>
        </header>
        <div className="sh-verticals">
          {VERTICALS.map((v) => (
            <article className={`sh-vertical sh-vertical--${v.tone}`} key={v.name}>
              <div className="sh-vertical-icon">{v.icon}</div>
              <div className="sh-vertical-copy">
                <h3>{v.name}</h3>
                <p>{v.blurb}</p>
              </div>
            </article>
          ))}
        </div>
        <div className="sh-what-footer">
          <p className="sh-what-aside">...always something different</p>
          <Link to="/products" className="sh-btn sh-btn--primary sh-what-products-link">
            See our products
          </Link>
        </div>
      </section>

      {/* ================= HOW WE WORK ================= */}
      <section className="sh-section sh-section--dark" id="how" data-nav-tone="dark">
        <header className="sh-section-head sh-process-head">
          <span className="sh-section-kicker">A clear path to launch</span>
          <h2>
            How we <em>work.</em>
          </h2>
        </header>
        <ol className="sh-process">
          {PROCESS.map((step, i) => (
            <li className={`sh-step sh-step--${i + 1}`} key={step.name}>
              <span className="sh-step-progress" aria-hidden="true" />
              <div className="sh-step-head">
                <span className="sh-step-number">0{i + 1}</span>
                <h3>{step.name}</h3>
              </div>
              <div className="sh-step-body">
                <div className="sh-step-glyph">{step.glyph}</div>
                <p>{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="sh-after">
          <p>
            <strong>We don&rsquo;t disappear after launch.</strong>
            {' '}
            Support, improvements, security updates, and the next release are part of the conversation.
          </p>
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section className="sh-section sh-section--plans" id="pricing" data-nav-tone="light">
        <div className="sh-plans-bg" aria-hidden="true">
          <PlansBg />
        </div>
        <header className="sh-section-head">
          <h2>
            Our <em>plans.</em>
          </h2>
        </header>
        <div className="sh-pricing">
          <article className="sh-plan">
            <div className="sh-plan-top">
              <h3>Let&rsquo;s begin</h3>
            </div>
            <div className="sh-plan-price">
              <span className="sh-plan-from">from</span>
              <span className="sh-plan-amount">$16,000</span>
            </div>
            <ul className="sh-plan-list">
              <li>A detailed scoped project</li>
              <li>Execution plan</li>
              <li>Internal beta every two weeks</li>
              <li>Launch on web, iOS, and Android</li>
            </ul>
            <button type="button" className="sh-btn sh-btn--primary sh-btn--full" onClick={() => openContactModal('demo')}>
              Book a call
            </button>
          </article>

          <article className="sh-plan">
            <div className="sh-plan-top">
              <h3>Let&rsquo;s partner</h3>
            </div>
            <div className="sh-plan-price">
              <span className="sh-plan-amount">Custom</span>
            </div>
            <ul className="sh-plan-list">
              <li>A dedicated team on your product</li>
              <li>Integrations with tools you use</li>
              <li>Support with real response times</li>
              <li>A roadmap we plan together</li>
            </ul>
            <button type="button" className="sh-btn sh-btn--primary sh-btn--full" onClick={() => openContactModal('consultation')}>
              Talk to us
            </button>
          </article>
        </div>
      </section>
    </div>
  );
}

export default Home;
